<template>
  <div class="studio-product">
    <div class="container-fluid text-center vh-100" v-if="isLoading">
      <HMSpinner class="mt-4" />
    </div>
    <StudioPlaylistToolbar
      v-if="product"
      :product="product"
      :showFullActions="showFullActions"
    />
    <HMStudioHeader
      class="d-md-none mb-3 overflow-hidden"
      :image="headerImage"
    />
    <div class="container my-3" v-if="product">
      <div class="back-icon-block mb-3" @click="returnToPlaylists()">
        <i class="back-icon fas fa-long-arrow-alt-left pr-1"></i>
        {{ $t('GENERAL.TEXT.BACK_TO') }} {{ product.product_name }}
      </div>
      <div class="row mb-3">
        <div class="col-12 d-flex flex-row align-items-center flex-wrap">
          <HMTag class="smaller mr-2" :title="$t('PRODUCTS.TITLE.PLAYLIST')" />
          <StudioPlaylistMediaTag
            class="text-dark"
            :playlist="playlist"
          ></StudioPlaylistMediaTag>
        </div>
        <div class="col-12 mt-2">
          <h1>{{ playlist.playlist_name }}</h1>
        </div>
        <div class="col-12 d-flex flex-wrap align-items-center mt-2">
          <HMInstructorsStack
            class="d-none d-md-flex"
            :instructors="product.instructors"
            size="40px"
          />
          <HMInstructorsStack
            class="d-flex d-md-none mr-2"
            :instructors="product.instructors"
            size="30px"
          />
          <span class="ml-md-2 c-charcoal py-1">{{
            $tc(
              'PRODUCTS.LABELS.INSTRUCTORS_VARIANT',
              product.instructors.length,
              { cnt: product.instructors.length }
            )
          }}</span>
        </div>
      </div>
    </div>
    <div class="container" v-if="!isLoading && product">
      <HMStudioHeader :image="headerImage" class="d-none d-md-block my-3" />
      <StudioPlaylistContent :playlist="playlist" :review="review" />
      <router-view class="mt-2"></router-view>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { errorToMessage } from '@/core/utils/errors';
import _ from 'lodash';
import RefreshData from '@/web/mixins/refreshData';
import { getCurrentUrl } from '@/web/utils/url';
import { getStudioURL } from '@/web/router/custom-domain-utils';
import {
  getRouteName,
  getRouteWithParams,
} from '@/core/api/utils/route-converter.ts';
import StudioPlaylistContent from '@/web/components/products/StudioPlaylistContent';
import StudioPlaylistMediaTag from '@/web/views/shop/StudioPlaylistMediaTag';
import StudioPlaylistToolbar from '@/web/views/shop/StudioPlaylistToolbar';

export default Vue.extend({
  name: 'StudioPlaylist',
  components: {
    StudioPlaylistContent,
    StudioPlaylistMediaTag,
    StudioPlaylistToolbar,
  },
  mixins: [RefreshData],
  props: {
    review: {
      type: Boolean,
      default: false,
    },
    prefix: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapGetters({
      product: 'product/product',
      studio: 'studio/currentStudio',
      playlist: 'product/playlist',
    }),
    studioURL() {
      return getStudioURL(this.$route);
    },
    productId() {
      return this.$route.params.product_id;
    },
    headerImage() {
      return this.playlist?.cover_file || this.product?.cover_file;
    },
  },
  data() {
    return {
      isLoading: true,
      selectedPage: this.$route.name,
      isPurchasing: false,
      showFullActions: false,
    };
  },
  methods: {
    getRouteWithParams(route, productId, prefix) {
      return getRouteWithParams(route, productId, prefix);
    },
    getRouteName(route, prefix) {
      return getRouteName(route, prefix);
    },
    onScroll(event) {
      this.showFullActions = 290 < window.scrollY;
    },
    async loadData() {
      this.isLoading = true;
      try {
        await Promise.all([
          this.$store.dispatch('product/getProductDetail', {
            studioURL: this.studioURL,
            productId: this.productId,
          }),
          this.$store.dispatch('product/getStudentPlaylist', {
            studioURL: this.studioURL,
            productId: this.productId,
            playlistId: this.$route.params.playlist_id,
          }),
        ]);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load product',
          text: errorToMessage(error.response, `Request failed: ${error}`),
        });
      } finally {
        this.isLoading = false;
      }
      try {
        this.$store.dispatch('product/trackProductView', {
          studioURL: this.studioURL,
          productId: this.productId,
        });
      } catch (e) {
        console.log('Error tracking product view');
      }
    },
    returnToPlaylists() {
      return this.$router.push({
        name: 'studio-product-playlists',
      });
    },
  },
  async mounted() {
    this.selectedPage = this.$route.name;
    await this.loadData();
    document.addEventListener('scroll', this.onScroll);
    this.onScroll();
  },
  destroyed() {
    document.removeEventListener('scroll', this.onScroll);
  },
  watch: {
    $route(to, from) {
      this.selectedPage =
        to.name || this.getRouteName('studio-product-about', this.prefix);
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.studio-product {
  @include media-breakpoint-up(md) {
    margin-top: $shop-topbar-lg-height;
  }
}

.studio-product-image {
  margin-top: 1rem;
  display: none;
  @include media-breakpoint-up(md) {
    display: flex;
  }
}

.back-icon {
  color: var(--brand-color, $coral);
}

.back-icon-block {
  cursor: pointer;
}
</style>
